import { useContext, useMemo } from 'react';
import Link from 'next/link';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { getDivisionName, getSelectedDivision } from '@/helpers/utils/siteUtils';
import SpecificationsTable from './SpecificationsTable';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../../../../customercenter-ui/components/ui/accordion';
import { ProductContext } from '../../contexts';
import { getFilteredAttribute } from '../../helpers/utils/productDataUtils';

export function AccordianSection() {
  const { product, initialSize } = useContext(ProductContext);
  const projectSettings: ProjectSettings = useProjSettings();
  const learnMoreURL = projectSettings?.shippingRestrictionsLearnMore ?? '';

  const shippingRestrictionEnabled = useMemo(() => {
    let hasShippingCategoryCode = false;
    let hasStateNotAvail = false;
    if (getFilteredAttribute('shippingCategoryCode', initialSize)) {
      hasShippingCategoryCode = true;
    }
    if (getFilteredAttribute('stateNotAvail', initialSize)?.length) {
      hasStateNotAvail = true;
    }
    return (
      getFilteredAttribute('signatureRequired', initialSize) ||
      hasShippingCategoryCode ||
      !getFilteredAttribute('akhiTerritoriesAvail', initialSize) ||
      !getFilteredAttribute('apoFpoAvail', initialSize) ||
      !getFilteredAttribute('priorityShipAvail', initialSize) ||
      !getFilteredAttribute('expressShipAvail', initialSize) ||
      !getFilteredAttribute('poBoxAvail', initialSize) ||
      hasStateNotAvail
    );
  }, [initialSize]);

  const notQualifyingItems = () => {
    const itemsArray = [];
    if (!getFilteredAttribute('akhiTerritoriesAvail', initialSize)) {
      itemsArray.push(<li key="akhiTerritoriesAvail">{mapLocaleToMeaningfulFormat()?.akhiTerritoriesAvailText}</li>);
    }
    if (!getFilteredAttribute('apoFpoAvail', initialSize)) {
      itemsArray.push(<li key="apoFpoAvail">{mapLocaleToMeaningfulFormat()?.apoFpoAvailText}</li>);
    }
    if (!getFilteredAttribute('poBoxAvail', initialSize)) {
      itemsArray.push(<li key="poBoxAvail">{mapLocaleToMeaningfulFormat()?.poBoxAvailText}</li>);
    }
    if (
      !getFilteredAttribute('priorityShipAvail', initialSize) ||
      !getFilteredAttribute('expressShipAvail', initialSize)
    ) {
      itemsArray.push(
        <li key="priorityShipAvail-expressShipAvail">{mapLocaleToMeaningfulFormat()?.priorityShipAvailText}</li>,
      );
    }
    if (getFilteredAttribute('forceLTLShip', initialSize)) {
      itemsArray.push(
        <li key="forceLTLShip">
          {
            mapLocaleToMeaningfulFormat([
              getFilteredAttribute('stateNotAvail', initialSize)
                ? getFilteredAttribute('stateNotAvail', initialSize)
                : '',
            ])?.forceLTLShipText
          }
        </li>,
      );
    }
    return itemsArray;
  };

  const learnMoreLink = () => {
    const division = getSelectedDivision() ?? 'ZB';
    const brandName = getDivisionName(division)?.toLowerCase();
    const newURL = learnMoreURL.replace('BrandName', brandName);
    return newURL;
  };

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value="item-1" className="border-0">
        <AccordionTrigger className="border-b text-lg font-bold leading-6">Description</AccordionTrigger>
        <AccordionContent className="pt-4">
          <div dangerouslySetInnerHTML={{ __html: product.description }}></div>
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="item-2" className="border-0">
        <AccordionTrigger className="border-b text-lg font-bold leading-6">Specifications</AccordionTrigger>
        <AccordionContent className="pt-4">
          <SpecificationsTable />
        </AccordionContent>
      </AccordionItem>

      {initialSize && shippingRestrictionEnabled && (
        <AccordionItem value="item-3" className="border-0">
          <AccordionTrigger className="border-b text-lg font-bold leading-6">Shipping Restrictions</AccordionTrigger>
          <AccordionContent className="pt-4">
            {getFilteredAttribute('shippingCategoryCode', initialSize) ? (
              <p>
                <strong>
                  {mapLocaleToMeaningfulFormat()?.shipCode} {getFilteredAttribute('shippingCategoryCode', initialSize)}{' '}
                </strong>
                -{' '}
                <Link className="underline hover:no-underline" target="_blank" href={learnMoreLink()}>
                  Learn More
                </Link>
              </p>
            ) : (
              <></>
            )}
            {getFilteredAttribute('signatureRequired', initialSize) ? (
              <p className="pt-3">
                <strong>This item requires a signature upon delivery.</strong>
              </p>
            ) : (
              <></>
            )}

            {getFilteredAttribute('forceLTLShip', initialSize) ? (
              <p className="pt-3">
                <strong>
                  This item is oversized. Upon shipment, you will be contacted within 2 business days to schedule
                  delivery.
                </strong>
              </p>
            ) : (
              <></>
            )}

            {notQualifyingItems().length ? (
              <>
                <p className="pt-3">
                  <strong>This item does NOT qualify for:</strong>
                </p>
                <ul className="list-disc p-2 px-5">
                  {notQualifyingItems().map((item: any) => {
                    return item;
                  })}
                </ul>
              </>
            ) : (
              <></>
            )}
          </AccordionContent>
        </AccordionItem>
      )}
    </Accordion>
  );
}
